import { Service } from '@toeverything/infra';

import type { WorkspaceServerService } from '../../cloud';
import { defaultWorkspaceApiClient } from '../../cloud/services/workspace-api-client';
import type { WorkspaceService } from '../../workspace';

export type AppAttachRequestKind = 'attach' | 'share' | 'access';
export interface AppAttachRequest {
  id: string;
  appBaseId: string;
  databaseId: string;
  docId: string;
  requesterId: string;
  requesterName: string | null;
  requesterEmail: string | null;
  kind: AppAttachRequestKind;
  generation: string;
  status: 'pending' | 'approved' | 'declined' | 'cancelled';
  expiresAt: string;
}

/** Requests to let an App use a table; a manager's approval runs the change on the server. */
export class AppAttachRequestService extends Service {
  constructor(
    private readonly workspace: WorkspaceService,
    private readonly server: WorkspaceServerService
  ) {
    super();
  }

  async request(input: {
    appBaseId: string;
    databaseId: string;
    kind: AppAttachRequestKind;
    payload: Record<string, unknown>;
  }) {
    const response = await this.fetch('', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return (await response.json()) as { id: string; status: 'pending' };
  }

  async listForDoc(docId: string, signal?: AbortSignal) {
    const query = new URLSearchParams({ docId });
    const response = await this.fetch(`?${query}`, { method: 'GET', signal });
    return (await response.json()) as AppAttachRequest[];
  }

  async listOwn(signal?: AbortSignal) {
    const response = await this.fetch('/mine', { method: 'GET', signal });
    return (await response.json()) as AppAttachRequest[];
  }

  async resolve(
    request: Pick<AppAttachRequest, 'id' | 'generation'>,
    decision: 'approve' | 'decline'
  ) {
    await this.fetch(`/${encodeURIComponent(request.id)}/resolve`, {
      method: 'POST',
      body: JSON.stringify({ generation: request.generation, decision }),
    });
  }

  private async fetch(suffix: string, init: RequestInit) {
    if (!this.server.server) throw new Error('Workspace server is unavailable');
    const workspaceId = this.workspace.workspace.id;
    return defaultWorkspaceApiClient.request({
      workspaceId,
      baseUrl: this.server.server.serverMetadata.baseUrl,
      path: `/api/workspace/${encodeURIComponent(workspaceId)}/app-attach-requests${suffix}`,
      init: {
        ...init,
        headers: { 'content-type': 'application/json', ...init.headers },
      },
      category: 'metadata',
      key: init.method === 'GET' ? `app-attach-request:${suffix}` : undefined,
      mode: 'foreground',
      priority: 'foreground',
    });
  }
}
